import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Input, OnChanges, OnInit, computed, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import { EventModel, FootballStats, PairGameScore } from '@frontend/sports/betting-offer/feature/model';
import { GridConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ShowInEpcotDirective } from '@frontend/sports/common/core/feature/show-in-epcot';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { ValueSliderDirective } from '@frontend/sports/common/ui/value-slider';
import { TrackingService, TrackingSource, trackingConstants } from '@frontend/sports/tracking/feature';
import { WidgetPage } from '@frontend/sports/types/components/widget';
import { ModularConfigAccessorService } from '@frontend/sports/widget/core/feature';
import { Observable, Subject, filter, switchMap, tap, timer } from 'rxjs';

enum ParticipantGoal {
    Participant1 = 'Participant1',
    Participant2 = 'Participant2',
}

@Component({
    selector: 'ms-pair-game-scoreboard',
    templateUrl: './pair-game-scoreboard.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, NgTemplateOutlet, ValueSliderDirective, ShowInEpcotDirective],
})
export class PairGameScoreboardComponent implements OnChanges, OnInit {
    sitecore = inject(Sitecore);
    private gridConfig = inject(GridConfig);
    private trackingService = inject(TrackingService);
    private configAccessor = inject(ModularConfigAccessorService, { optional: true });

    @Input() showRedcards = false;
    @Input() event: EventModel;
    @Input() tracking: TrackingSource;

    statistics: FootballStats;
    redCards: number[] = [];
    yellowCards: number[] = [];
    isGoalAnimationEnabled = false;

    @HostBinding('class') className = 'pair-game';

    @HostBinding('class.hidden')
    get hidden(): boolean {
        return this.score?.participant1 === undefined || this.score?.participant2 === undefined;
    }

    private _goal$ = new Subject<ParticipantGoal | null>();
    goalParticipant1 = toSignal(this.filterGoal(ParticipantGoal.Participant1), {
        initialValue: null,
    });

    goalParticipant2 = toSignal(this.filterGoal(ParticipantGoal.Participant2), {
        initialValue: null,
    });

    recentGoalScored = computed(() => this.goalParticipant1() ?? this.goalParticipant2());

    constructor() {
        this._goal$
            .pipe(
                filter((v) => !!v),
                tap(() => this.trackGoalAnimation()),
                switchMap(() => timer(this.gridConfig.goalAnimationTimeInterval)),
                takeUntilDestroyed(),
            )
            .subscribe(() => this._goal$.next(null));
    }

    ngOnInit() {
        this.isGoalAnimationEnabled = this.gridConfig.isGoalAnimationEnabled.includes(this.event.sport.id);
    }

    ngOnChanges(changes: ISimpleChanges<PairGameScoreboardComponent>): void {
        if (this.isGoalAnimationEnabled) {
            this.handleScoreboardChanges(changes);
        }

        this.statistics = this.event.scoreboard.statistics as FootballStats;
        if (this.statistics) {
            if (this.showRedcards) {
                this.redCards = [this.statistics.redcards?.totalPoints?.participant1 || 0, this.statistics.redcards?.totalPoints?.participant2 || 0];
            }
        }
    }

    get score(): PairGameScore | undefined {
        if (this.event.scoreboard.score) {
            return this.event.scoreboard.score as PairGameScore;
        }

        return undefined;
    }

    get serving(): number {
        if (this.score && this.score.hasServing) {
            return this.score.currentlyServing;
        }

        return 0;
    }

    private handleScoreboardChanges(changes: ISimpleChanges<PairGameScoreboardComponent>): void {
        const currentScore = changes.event?.currentValue.scoreboard?.score as PairGameScore;
        const previousScore = changes.event?.previousValue?.scoreboard?.score as PairGameScore;

        const prevP1Score = previousScore?.participant1;
        const currP1Score = currentScore?.participant1;
        const prevP2Score = previousScore?.participant2;
        const currP2Score = currentScore?.participant2;

        if (prevP1Score !== undefined && currP1Score !== undefined && prevP1Score < currP1Score) {
            this._goal$.next(ParticipantGoal.Participant1);
        }
        if (prevP2Score !== undefined && currP2Score !== undefined && prevP2Score < currP2Score) {
            this._goal$.next(ParticipantGoal.Participant2);
        }
    }

    private filterGoal(participant: ParticipantGoal): Observable<ParticipantGoal | null> {
        return this._goal$.pipe(filter((p) => !p || p === participant));
    }

    private trackGoalAnimation(): void {
        return this.trackingService.track(trackingConstants.EVENT_CONTENT_VIEW, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: this.tracking.source
                ? this.getCategoryEvent(this.configAccessor?.getPage(), this.tracking.source)
                : '',
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'sport events',
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'load',
            [trackingConstants.COMPONENT_POSITION_EVENT]: this.event.name,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getTrackingLocationEvent(),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: 'goal animation',
            [trackingConstants.COMPONENT_CONTENT_POSITION]: this.tracking.position,
        });
    }

    private getTrackingLocationEvent(): string {
        return [this.configAccessor?.getWidget()?.templateName, this.event.league.name, this.event.league.id]
            .filter((segment) => segment)
            .join('/')
            .toLowerCase();
    }

    private getCategoryEvent(categoryEvent: WidgetPage | undefined, source: string): string {
        const trackingSource = source.split('/')[0];
        if (categoryEvent) {
            return `${categoryEvent} grid widgets`;
        }

        return `${trackingSource} grids`;
    }
}
